import { IAppContextActions } from 'contexts/App/types'
import { LogOut } from '../AuthService'
import { IMethod } from './Headers'
import { IBaseResponse, MessageError } from './Response'

const apiUrl = process.env.REACT_APP_PROXY_URL

const campaignApiKey = process.env.REACT_APP_CAMPAIGN_EXECUTE_API_KEY || ''
const headers = new Headers()

const getRequestInit = <TBody>(
  method: IMethod,
  body?: TBody,
  customRequestInit?: RequestInit,
): RequestInit => {
  return {
    method,
    headers: headers,
    cache: 'no-cache',
    body: body ? JSON.stringify(body) : undefined,
    ...customRequestInit,
  }
}

const sendRequest = (
  url: string,
  requestInit: RequestInit,
): Promise<Response> => fetch(url, requestInit)

export const appendHeader = (prop: string, value: string) => {
  if (headers.has(prop)) {
    headers.set(prop, value)
  } else {
    headers.append(prop, value)
  }
}

const handleError = (resultError: MessageError) => {
  if (resultError && resultError.detail && resultError.detail.errors) {
    const { errors } = resultError.detail
    return !!errors.length ? errors[0].detail : ''
  }
  return ''
}

async function removeSignedToken(dispatch: React.Dispatch<IAppContextActions>) {
  await LogOut()

  dispatch({
    type: 'updateApp',
    data: { signed: false },
  })
}

interface THeaders {
  access_key: string
  secret_key: string
}

export const json = async <TResponse, TRequest = unknown>(
  method: IMethod,
  path: string,
  dispatch: React.Dispatch<IAppContextActions>,
  body?: TRequest,
  headers?: THeaders,
  options?: {
    baseUrl?: string
    customRequestInit?: RequestInit
  },
): Promise<IBaseResponse<TResponse>> => {
  try {
    const token = localStorage.getItem('@studio:user_access_token')
    const botId = localStorage.getItem('@studio:agent_id') || ''
    const baseUrl = options?.baseUrl ?? apiUrl
    const url = `${baseUrl}${path}`

    if (!token) {
      await removeSignedToken(dispatch)
    }

    if (!!token) {
      appendHeader('Content-Type', 'application/json')
      appendHeader('Authorization', token)
      appendHeader('x-bot-id', botId)
    }

    if (/\/execute/.test(path)) {
      appendHeader('x-api-key', campaignApiKey)
    }

    if (/falazap\/identity-token/.test(path) && headers) {
      appendHeader(`x-access-key`, headers.access_key)
      appendHeader(`x-secret-key`, headers.secret_key)
    }

    const requestInit = getRequestInit<TRequest>(
      method,
      body,
      options?.customRequestInit,
    )

    const result = await sendRequest(url, requestInit)

    if (!result.ok) {
      const errors: MessageError = await result.json()
      throw new Error(handleError(errors))
    }

    const Data: TResponse = await result.json()

    return {
      Data,
      StatusCode: result.status,
      Success: true,
      Message: '',
    }
  } catch (error: any) {
    return {
      Data: {} as TResponse,
      StatusCode: 400,
      Success: false,
      Message: error.message,
    }
  }
}
